import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  User,
  Building2,
  FileText,
  Receipt,
  Save,
  AlertCircle,
  CheckCircle2,
  Info,
  Pencil,
  Loader2
} from 'lucide-react';

const ProfilePage = () => {
  const { user, refreshUser, isTechnician } = useAuth();
  const [fiscalSettings, setFiscalSettings] = useState(null);
  const [fiscalOptions, setFiscalOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    title: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    if (user) {
      setProfileData({
        title: user.title || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, optionsRes] = await Promise.all([
        userApi.getMyFiscalSettings(),
        userApi.getFiscalRegimes()
      ]);
      setFiscalSettings(settingsRes.data);
      setFiscalOptions(optionsRes.data);
    } catch (error) {
      console.error('Error loading fiscal settings:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await userApi.updateMyFiscalSettings(fiscalSettings);
      toast.success('Impostazioni fiscali salvate');
    } catch (error) {
      console.error('Error saving fiscal settings:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await userApi.updateMyProfile(profileData);
      toast.success('Profilo aggiornato con successo');
      setIsEditingProfile(false);
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio del profilo');
    } finally {
      setSavingProfile(false);
    }
  };
  
  const handleCancelEdit = () => {
    setProfileData({
      title: user?.title || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || ''
    });
    setIsEditingProfile(false);
  };

  const updateField = (field, value) => {
    setFiscalSettings(prev => ({ ...prev, [field]: value }));
  };

  // Get regime details
  const getRegimeInfo = (regimeValue) => {
    if (!fiscalOptions?.regimi) return null;
    return fiscalOptions.regimi.find(r => r.value === regimeValue);
  };

  const getCassaInfo = (cassaValue) => {
    if (!fiscalOptions?.casse) return null;
    return fiscalOptions.casse.find(c => c.value === cassaValue);
  };

  const isConfigured = fiscalSettings?.regime_fiscale && fiscalSettings.regime_fiscale !== 'non_configurato';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="text-h2">Profilo</h1>
        <p className="text-body-small mt-1">Gestisci le tue informazioni e impostazioni fiscali</p>
      </div>

      {/* User Info Card - Editable */}
      <Card className="card-abitalo">
        <CardHeader className="border-b border-[#DCDCDC]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-[#FF7819]" />
              Informazioni Personali
            </CardTitle>
            {!isEditingProfile ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditingProfile(true)}
                className="text-[#FF7819] border-[#FF7819] hover:bg-[#FF7819] hover:text-white"
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Modifica
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={savingProfile}
                >
                  Annulla
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="bg-[#FF7819] hover:bg-[#e56a10]"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salva
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {isEditingProfile ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titolo</Label>
                <Input
                  id="title"
                  value={profileData.title}
                  onChange={(e) => setProfileData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ing., Arch., Geom., Dr., ..."
                  data-testid="profile-title"
                />
                <p className="text-xs text-[#737373]">Titolo professionale (opzionale)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="first_name">Nome</Label>
                <Input
                  id="first_name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Il tuo nome"
                  data-testid="profile-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Cognome</Label>
                <Input
                  id="last_name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Il tuo cognome"
                  data-testid="profile-last-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-[#737373]">L'email non può essere modificata</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefono</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+39 ..."
                  data-testid="profile-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Input
                  value={user?.role?.replace('_', ' ') || ''}
                  disabled
                  className="bg-gray-50 capitalize"
                />
                <p className="text-xs text-[#737373]">Il ruolo è assegnato dall'amministratore</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#737373]">Nome</Label>
                <p className="font-medium">
                  {user?.title && <span className="text-[#FF7819]">{user.title} </span>}
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
              <div>
                <Label className="text-[#737373]">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <Label className="text-[#737373]">Ruolo</Label>
                <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <div>
                <Label className="text-[#737373]">Telefono</Label>
                <p className="font-medium">{user?.phone || '-'}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fiscal Settings - Only for Technicians */}
      {isTechnician && fiscalSettings && (
        <>
          {/* Status Banner */}
          {!isConfigured && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Configura il tuo regime fiscale</p>
                <p className="text-sm text-amber-700 mt-1">
                  Per poter applicare correttamente IVA e contributi sui preventivi, configura le tue impostazioni fiscali.
                </p>
              </div>
            </div>
          )}

          {isConfigured && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">Regime fiscale configurato</p>
                <p className="text-sm text-green-700 mt-1">
                  {getRegimeInfo(fiscalSettings.regime_fiscale)?.label}
                </p>
              </div>
            </div>
          )}

          {/* Fiscal Settings Card */}
          <Card className="card-abitalo">
            <CardHeader className="border-b border-[#DCDCDC]">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#FF7819]" />
                Impostazioni Fiscali
              </CardTitle>
              <CardDescription>
                Configura il tuo regime fiscale per la fatturazione
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Regime Fiscale */}
              <div className="space-y-2">
                <Label htmlFor="regime_fiscale">Regime Fiscale *</Label>
                <Select
                  value={fiscalSettings.regime_fiscale || 'non_configurato'}
                  onValueChange={(value) => updateField('regime_fiscale', value)}
                >
                  <SelectTrigger data-testid="regime-fiscale-select">
                    <SelectValue placeholder="Seleziona regime fiscale" />
                  </SelectTrigger>
                  <SelectContent>
                    {fiscalOptions?.regimi?.map((regime) => (
                      <SelectItem key={regime.value} value={regime.value}>
                        <div>
                          <span>{regime.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fiscalSettings.regime_fiscale && fiscalSettings.regime_fiscale !== 'non_configurato' && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 rounded-lg">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      {getRegimeInfo(fiscalSettings.regime_fiscale)?.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Cassa Previdenziale - Only for Regime Ordinario */}
              {['ordinario', 'ordinario_semplificato'].includes(fiscalSettings.regime_fiscale) && (
                <div className="space-y-4 p-4 bg-[#FAFAFA] rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="cassa_previdenziale">Cassa Previdenziale</Label>
                    <Select
                      value={fiscalSettings.cassa_previdenziale || 'nessuna'}
                      onValueChange={(value) => {
                        updateField('cassa_previdenziale', value);
                        // Set default percentage
                        const cassa = getCassaInfo(value);
                        if (cassa) {
                          updateField('percentuale_cassa', cassa.percentuale_default);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="cassa-select">
                        <SelectValue placeholder="Seleziona cassa previdenziale" />
                      </SelectTrigger>
                      <SelectContent>
                        {fiscalOptions?.casse?.map((cassa) => (
                          <SelectItem key={cassa.value} value={cassa.value}>
                            {cassa.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {fiscalSettings.cassa_previdenziale && fiscalSettings.cassa_previdenziale !== 'nessuna' && (
                    <div className="space-y-2">
                      <Label htmlFor="percentuale_cassa">Percentuale Rivalsa Cassa (%)</Label>
                      <Input
                        id="percentuale_cassa"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={fiscalSettings.percentuale_cassa || 4}
                        onChange={(e) => updateField('percentuale_cassa', parseFloat(e.target.value) || 0)}
                        className="max-w-[120px]"
                        data-testid="percentuale-cassa-input"
                      />
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Dati Fatturazione */}
              <div>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#FF7819]" />
                  Dati Fatturazione
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="partita_iva">Partita IVA</Label>
                    <Input
                      id="partita_iva"
                      value={fiscalSettings.partita_iva || ''}
                      onChange={(e) => updateField('partita_iva', e.target.value)}
                      placeholder="IT12345678901"
                      data-testid="partita-iva-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                    <Input
                      id="codice_fiscale"
                      value={fiscalSettings.codice_fiscale || ''}
                      onChange={(e) => updateField('codice_fiscale', e.target.value.toUpperCase())}
                      placeholder="RSSMRA80A01H501U"
                      data-testid="codice-fiscale-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="indirizzo_fatturazione">Indirizzo Fatturazione</Label>
                    <Input
                      id="indirizzo_fatturazione"
                      value={fiscalSettings.indirizzo_fatturazione || ''}
                      onChange={(e) => updateField('indirizzo_fatturazione', e.target.value)}
                      placeholder="Via Roma 1, 00100 Roma (RM)"
                      data-testid="indirizzo-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pec">PEC</Label>
                    <Input
                      id="pec"
                      type="email"
                      value={fiscalSettings.pec || ''}
                      onChange={(e) => updateField('pec', e.target.value)}
                      placeholder="nome@pec.it"
                      data-testid="pec-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codice_sdi">Codice SDI</Label>
                    <Input
                      id="codice_sdi"
                      value={fiscalSettings.codice_sdi || ''}
                      onChange={(e) => updateField('codice_sdi', e.target.value.toUpperCase())}
                      placeholder="XXXXXXX"
                      maxLength={7}
                      data-testid="codice-sdi-input"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-fiscal-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          {isConfigured && (
            <Card className="card-abitalo">
              <CardHeader className="border-b border-[#DCDCDC]">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#FF7819]" />
                  Anteprima Applicazione Fiscale
                </CardTitle>
                <CardDescription>
                  Esempio di come verranno calcolati i totali sui preventivi
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-[#FAFAFA] rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">Imponibile (esempio)</span>
                    <span className="font-medium">€ 10.000,00</span>
                  </div>
                  
                  {/* Cassa previdenziale */}
                  {['ordinario', 'ordinario_semplificato'].includes(fiscalSettings.regime_fiscale) && 
                   fiscalSettings.cassa_previdenziale && fiscalSettings.cassa_previdenziale !== 'nessuna' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#737373]">
                        Rivalsa {getCassaInfo(fiscalSettings.cassa_previdenziale)?.label} ({fiscalSettings.percentuale_cassa}%)
                      </span>
                      <span className="font-medium">€ {(10000 * (fiscalSettings.percentuale_cassa / 100)).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* IVA */}
                  {['ordinario', 'ordinario_semplificato'].includes(fiscalSettings.regime_fiscale) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#737373]">IVA 22%</span>
                      <span className="font-medium">
                        € {(
                          (10000 + (fiscalSettings.cassa_previdenziale !== 'nessuna' ? 10000 * (fiscalSettings.percentuale_cassa / 100) : 0)) * 0.22
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  {/* Bollo forfettario */}
                  {['forfettario', 'forfettario_start_up', 'minimi'].includes(fiscalSettings.regime_fiscale) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#737373]">Marca da bollo (se &gt; €77.47)</span>
                      <span className="font-medium">€ 2,00</span>
                    </div>
                  )}
                  
                  <Separator />
                  
                  {/* Totale */}
                  <div className="flex justify-between font-semibold">
                    <span>Totale</span>
                    <span className="text-[#FF7819]">
                      {['ordinario', 'ordinario_semplificato'].includes(fiscalSettings.regime_fiscale) ? (
                        `€ ${(
                          10000 + 
                          (fiscalSettings.cassa_previdenziale !== 'nessuna' ? 10000 * (fiscalSettings.percentuale_cassa / 100) : 0) +
                          ((10000 + (fiscalSettings.cassa_previdenziale !== 'nessuna' ? 10000 * (fiscalSettings.percentuale_cassa / 100) : 0)) * 0.22)
                        ).toFixed(2)}`
                      ) : (
                        '€ 10.002,00'
                      )}
                    </span>
                  </div>
                </div>
                
                <p className="text-xs text-[#737373] mt-4">
                  * I calcoli mostrati sono puramente indicativi. Il totale effettivo dipenderà dall'imponibile del preventivo.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Non-technician message */}
      {!isTechnician && (
        <Card className="card-abitalo">
          <CardContent className="p-6 text-center">
            <p className="text-[#737373]">
              Le impostazioni fiscali sono disponibili solo per i tecnici esterni.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfilePage;
